<template>
    <b-card no-body class="declaration-details-section">
        <b-card-body>
            <h4 class="mb-2">{{ translate(
                'Page(ReceivedRequest):Component(ReviewAndSubmit):declaration-details',
                'Declaration details',
            ) }}</h4>

            <div class="declaration-details-section__field">
                <div class="declaration-details-section__heading">
                    <div class="d-flex align-items-center flex-wrap">
                        <h5 class="mb-0 mr-1">{{ translate(
                            'Page(ReceivedRequest):Component(ReviewAndSubmit):customer-comment',
                            'Comment included in the declaration',
                        ) }}</h5>
                        <b-badge variant="light-secondary">{{ optionalLabel }}</b-badge>
                    </div>
                    <b-badge variant="light-secondary">
                        <feather-icon icon="EyeIcon" size="14" class="mr-50" />
                        {{ translate(
                            'Page(ReceivedRequest):Component(ReviewAndSubmit):visible-to-customer',
                            'Visible to customer',
                        ) }}
                    </b-badge>
                </div>
                <p class="text-less-muted mt-50 mb-1">{{ translate(
                    'Page(ReceivedRequest):Component(ReviewAndSubmit):customer-comment-description',
                    'Add a comment, explanation or limitation that should accompany your declaration.',
                ) }}</p>
                <b-form-textarea
                    :value="customerInformation"
                    rows="4"
                    :disabled="disabled"
                    :placeholder="translate(
                        'Page(ReceivedRequest):Component(ReviewAndSubmit):customer-comment-placeholder',
                        'Write a comment about this declaration...',
                    )"
                    @input="update('customerInformation', $event)"
                />
                <small class="form-text text-less-muted">{{ translate(
                    'Page(ReceivedRequest):Component(ReviewAndSubmit):customer-comment-example',
                    'For example: applicable product versions, excluded components, document scope or other relevant limitations.',
                ) }}</small>
            </div>

            <hr>

            <div class="declaration-details-section__field">
                <div class="declaration-details-section__heading">
                    <div class="d-flex align-items-center flex-wrap">
                        <h5 class="mb-0 mr-1">{{ translate(
                            'Page(ReceivedRequest):Component(ReviewAndSubmit):internal-note',
                            'Internal note',
                        ) }}</h5>
                        <b-badge variant="light-secondary">{{ optionalLabel }}</b-badge>
                    </div>
                    <b-badge variant="light-secondary">
                        <feather-icon icon="LockIcon" size="14" class="mr-50" />
                        {{ translate(
                            'Page(ReceivedRequest):Component(ReviewAndSubmit):visible-to-organization',
                            'Only visible to your organisation',
                        ) }}
                    </b-badge>
                </div>
                <p class="text-less-muted mt-50 mb-1">{{ translate(
                    'Page(ReceivedRequest):Component(ReviewAndSubmit):internal-note-description',
                    'This note will not be included in the declaration or shown to the customer.',
                ) }}</p>
                <b-form-textarea
                    :value="internalComment"
                    rows="3"
                    :disabled="disabled"
                    :placeholder="translate(
                        'Page(ReceivedRequest):Component(ReviewAndSubmit):internal-note-placeholder',
                        'Add an internal note...',
                    )"
                    @input="update('internalComment', $event)"
                />
            </div>

            <div class="declaration-details-section__validity mt-2">
                <h5>{{ translate(
                    'Page(ReceivedRequest):Component(ReviewAndSubmit):declaration-validity',
                    'Declaration validity',
                ) }}</h5>
                <p class="text-less-muted mb-1">{{ translate(
                    'Page(ReceivedRequest):Component(ReviewAndSubmit):declaration-validity-description',
                    'Chemity will remind you when the declaration needs to be reviewed.',
                ) }}</p>
                <b-row>
                    <b-col cols="12" md="6">
                        <b-form-group
                            class="mb-0"
                            :label="translate(
                                'Page(ReceivedRequest):Component(ReviewAndSubmit):validity-period',
                                'Validity period',
                            )"
                        >
                            <b-form-select
                                :value="validityMonth"
                                :options="validityOptions"
                                text-field="title"
                                value-field="month"
                                :disabled="disabled"
                                @change="updateValidity"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col cols="12" md="6">
                        <b-form-group
                            class="mb-0"
                            :label="translate(
                                'Page(ReceivedRequest):Component(ReviewAndSubmit):valid-until',
                                'Valid until',
                            )"
                        >
                            <b-form-input :value="validUntil" readonly />
                        </b-form-group>
                    </b-col>
                </b-row>
            </div>
        </b-card-body>
    </b-card>
</template>

<script>
import {
    BBadge,
    BCard,
    BCardBody,
    BCol,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormTextarea,
    BRow,
} from 'bootstrap-vue'

export default {
    name: 'DeclarationDetailsSection',
    components: {
        BBadge,
        BCard,
        BCardBody,
        BCol,
        BFormGroup,
        BFormInput,
        BFormSelect,
        BFormTextarea,
        BRow,
    },
    props: {
        value: { type: Object, default: () => ({}) },
        disabled: { type: Boolean, default: false },
    },
    computed: {
        customerInformation() {
            return this.value.customerInformation || ''
        },
        internalComment() {
            return this.value.internalComment || ''
        },
        validityMonth() {
            const validity = this.value.declarationValidity
            return validity && typeof validity === 'object' ? validity.month : validity
        },
        validityOptions() {
            const options = this.$store && this.$store.getters.declarationOfValidityOptions
                ? this.$store.getters.declarationOfValidityOptions
                : []
            const monthsLabel = this.translate('Component(Text):months', 'months')

            return options.map(option => ({
                ...option,
                title: `${option.month} ${monthsLabel}`,
            }))
        },
        validUntil() {
            const months = Number(this.validityMonth)
            if (!months) {
                return ''
            }

            return this.$moment().add(months, 'months').format('D MMMM YYYY')
        },
        optionalLabel() {
            return this.translate('Component(Text):optional', 'Optional')
        },
    },
    mounted() {
        if (!this.validityMonth && this.validityOptions.length > 0) {
            this.updateValidity(this.validityOptions[0].month)
        }
    },
    methods: {
        translate(key, fallback) {
            const translation = this.$t(key)
            return translation && translation !== key ? translation : fallback
        },
        update(field, value) {
            this.$emit('input', { ...this.value, [field]: value })
        },
        updateValidity(month) {
            const declarationValidity = this.validityOptions.find(option => option.month === month) || null
            this.update('declarationValidity', declarationValidity)
        },
    },
}
</script>

<style lang="scss" scoped>
.declaration-details-section {
    border-color: #ebe9f1;

    &__heading {
        align-items: center;
        display: flex;
        gap: 1rem;
        justify-content: space-between;
    }

}

@media (max-width: 767.98px) {
    .declaration-details-section__heading {
        align-items: flex-start;
        flex-direction: column;
    }
}
</style>
